import { useLayoutEffect, useRef, useState } from "react";

// Measures an element's content-box width with a ResizeObserver. The width changes
// whenever the resizable panels are dragged (the center column resizes), which is what
// drives the fixed-logical-size scale in Slide.tsx — this hook ties resizing to scaling.
export function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    // Seed before paint so the first frame isn't a zero-width one. This is a layout
    // effect for exactly that reason: `useEffect` runs AFTER the browser paints, so the
    // mount frame rendered at scale 0 — invisible. Barely noticeable in the editor, but
    // the presentation re-mounts the outgoing slide into the `.slide-leaving` layer at
    // the instant a transition starts, so that blank frame was the black flash under a
    // covering slide (§10).
    setWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
}
